import { ChangeEvent, useCallback, useMemo, useState } from "react";
import { createPaginationQueryString } from "../../../Queries/queryHelpers";
import { useDebouncedEffect } from "../../../State/Hooks/useDebouncedEffect";
import { IPaginationQuery } from "../../../Typings/MessageText";
import { PaginationValues } from "../../../Typings/Pagination";
import { getSessionStorage, setSessionStorage } from "../../../Utilities/browserStorage";

type PaginationWithSearch = Partial<PaginationValues & { search: string }>;

const paginationDefaults: PaginationWithSearch = {
  page: 0,
  pageSize: 10,
  sortBy: "createdDate",
  sortDirection: "asc" as "asc" | "desc",
  search: "",
};

/**
 * Re-useable hook for managing pagination state for shallow components
 * @param defaults - Allow consumer to set default values for pagination
 * @param key - Key to store pagination state in session storage *must be unique per component*
 * @returns PaginationState
 */
export function usePaginationState(defaults = paginationDefaults, key = "pagination") {
  const defaultState = getSessionStorage<PaginationWithSearch>(key) ?? defaults;

  const [page, setPage] = useState<number>(defaultState.page ?? 0);
  const [pageSize, setPageSize] = useState<number>(defaultState.pageSize ?? 10);
  const [search, setSearch] = useState<string>(defaultState.search ?? "");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">(defaultState.sortDirection ?? "asc");
  const [sortBy, setSortBy] = useState<string>(defaultState.sortBy ?? "createdDate");
  const paginationQuery = useMemo(
    () =>
      ({
        page,
        pageSize,
        sortBy,
        sortDirection,
        search,
      }) as IPaginationQuery,
    [page, pageSize, sortBy, sortDirection, search],
  );
  const getQueryString = useCallback(
    (customQuery?: Partial<IPaginationQuery>) =>
      createPaginationQueryString({
        ...paginationQuery,
        ...customQuery,
        page: Math.max(customQuery?.page ?? page, 1),
      }),
    [paginationQuery, page],
  );

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
    setPage(0);
  };

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangePageSize = (event: ChangeEvent<HTMLInputElement>) => {
    setPageSize(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRequestSort = (property: string) => {
    const isAsc = sortBy === property && sortDirection === "asc";
    setSortDirection(isAsc ? "desc" : "asc");
    setSortBy(property);
    setPage(0);
  };

  useDebouncedEffect(
    () => {
      setSessionStorage<PaginationWithSearch>(key, { page, pageSize, sortBy, sortDirection, search });
    },
    [page, pageSize, sortBy, sortDirection, search],
    100,
  );

  return {
    getters: { page, pageSize, sortBy, sortDirection, search, paginationQuery, getQueryString },
    setters: { setPage, setPageSize, setSortBy, setSortDirection, setSearch },
    handlers: {
      handleChangePage,
      handleChangePageSize,
      handleRequestSort,
      handleSearchChange,
    },
  } as const;
}

export type PaginationState = ReturnType<typeof usePaginationState>;
